import { useCallback, useState } from 'react'
import { useLocation } from 'react-router'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { parseCriteriaFromSearch, serializeCriteriaToSearch } from '@Components/Search/SearchPanel/criteria-url-params'
import useUrlSearchWriter from '@Components/Search/SearchPanel/useUrlSearchWriter'

export interface UseSearchCriteriaUrlSyncResult {
    /**
     * Criteria parsed from the URL's query string on first render, in the shape `mapCriteriaToObject`
     * produces; undefined if none of the options' keys were present. Read once — after the page has
     * mounted the URL is a reflection of in-app state (via `syncCriteriaToUrl`), not re-read live.
     */
    urlCriteria: Record<string, unknown> | undefined
    /**
     * Writes the submitted criteria into the URL's query string, so the address bar is always a valid
     * link back to this search. Pass `undefined` to clear them (e.g. on "Reset Search"). Filters and any
     * other params, and the current hash, are preserved.
     */
    syncCriteriaToUrl: (criteria: object | undefined) => void
}

/**
 * Keeps a SearchPanel's submitted search terms reflected in the URL's query string, the sibling of
 * `useFilterUrlSync` — the two compose on one page and share a URL writer so applying a search that
 * changes both doesn't lose one.
 *
 * Opt-in glue a page wires itself: build the criteria store with `applyCriteriaFromObject(urlCriteria)`,
 * seed the page's first search from the same object, call `syncCriteriaToUrl` from `onSearch`, and
 * `syncCriteriaToUrl(undefined)` from `onReset`.
 *
 * @param options - The criteria options the page's criteria store was built with.
 */
export const useSearchCriteriaUrlSync = (options: Array<SelectOptionHide<any>>): UseSearchCriteriaUrlSyncResult => {
    const location = useLocation()
    const writeSearch = useUrlSearchWriter()

    const [urlCriteria] = useState(() => parseCriteriaFromSearch(location.search, options))

    const syncCriteriaToUrl = useCallback(
        (criteria: object | undefined) =>
            writeSearch((currentSearch) => serializeCriteriaToSearch(currentSearch, criteria, options)),
        [options, writeSearch]
    )

    return { urlCriteria, syncCriteriaToUrl }
}

export default useSearchCriteriaUrlSync
